import { themeColors } from "../../themeColors";
/**
 * @file  src\mobile\pages\AgentClientListing.js
 * @brief  This file is responsible for agent clients listing
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, InputGroup, Button, Table } from "react-bootstrap";
import SearchIcon from "../components/searchIcn";
import SortClientoffcanvas from "../components/SortClientoffcanvas";
import "../styles/clients.scss";
import { getClientsList } from "../../services/agentClientsService";
import {
  checkTutorialDisplayStatus,
  saveTutorialStatusToLocal,
  setLocalStorage,
  truncate,
} from "../../helpers/common";
import AddNewClient from "../commonComponents/ClientPopup";
import { pageURLs } from "@/constants";
import { CLIENT_DETAILS_KEY, PAGE_SIZE } from "../../constants/common";
import LearnIntroductionModal from "../components/LearnIntroductionModal";
import { saveTutorialStatus } from "../../services/commonServices";
import JoyRide, { STATUS } from "react-joyride";
import IntroductionTutorialIconStep1 from "../../images/tut-quickview-icon.svg";
import IntroductionTutorialIconStep2 from "../../images/tut-performing-icon.svg";
import DocumentationTutorialMob from "../../components/Tutorial/DocumentationTutorialMob";
import HandleScroll from "../commonComponents/HandleScroll";
import TopHeader from "../components/common/TopHeader";
import { usePremium } from "@/hooks";
import BackLinkButton from "../components/BackLinkButton";

const AgentClientsListing = (props) => {
  const scrollStates = HandleScroll();
  const isReachedBottom = _.get(scrollStates, "isReachedBottom", null);
  const navigate = useNavigate();
  const tutorialPage = 4;
  const [showAddNewClient, setAddNewClientShow] = useState(false);
  const handleAddNewClientClose = () => {
    setAddNewClientShow(false);
    setCurrPage(1);
    setPrevPage(0);
    setList([]);
  };
  const handleAddNewClientShow = () => setAddNewClientShow(true);
  const [displayTutorial, setDisplayTutorial] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const myRef = useRef(null);
  const [itemList, setList] = useState([]); // storing list
  const [showNoResult, setShowNoResult] = useState(false);
  const [currPage, setCurrPage] = useState(1); // storing current page number
  const [prevPage, setPrevPage] = useState(0); // storing prev page number

  const { shouldToggleColor } = usePremium();

  const [sort, setSort] = useState({ order: "asc", orderBy: "firstName" });
  const [inputSearchValue, setInputSearchValue] = useState("");
  const handleSearchChange = (event) => {
    const searchtext = event.target.value;
    setInputSearchValue(searchtext);
    setCurrPage(1);
    setPrevPage(0);
    setList([]);
  };
  const onClickSearchClose = () => {
    setInputSearchValue("");

    setShow(!show);
    setCurrPage(1);
    setPrevPage(0);
    setList([]);
  };

  useEffect(() => {
    document
      .getElementById("scroll-chain")
      .classList.add("scroll-chaining-fix"); // Scroll chaing fix in iphone(jumping effect)
    setTimeout(function () {
      let tutorialDisplayStatus = checkTutorialDisplayStatus(tutorialPage);
      setShowTutorialModal(tutorialDisplayStatus);
    }, 300);
  }, []);

  useEffect(() => {
    if (isReachedBottom) {
      setCurrPage((prevState) => prevState + 1);
    }
  }, [isReachedBottom]);

  const fetchData = async (data) => {
    setShowNoResult(true);
    await getClientsList(data)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult.length > 0) {
          setPrevPage(currPage);
          setList([...itemList, ...responseResult]);
        } else {
          if (itemList.length == 0 && inputSearchValue.length == 0) {
            setShowNoResult(false);
          }
          return;
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  useEffect(() => {
    document.body.classList.add("bg-offcanvas-fix");
  }, []);

  const [showSortClient, setShowSortClient] = useState(false);
  const SortClientClose = () => setShowSortClient(false);
  const SortClientShow = () => setShowSortClient(true);

  const [show, setShow] = useState(false);
  const onClickHandler = () => {
    setShow(!show);
  };

  const handleSort = ({ order, orderBy }) => {
    setSort({ order: order, orderBy: orderBy });
    setCurrPage(1);
    setPrevPage(0);
    setList([]);
    SortClientClose();
  };

  useEffect(() => {
    if (currPage !== prevPage) {
      const data = {
        sortBy: sort.orderBy,
        search: inputSearchValue,
        sortDir: sort.order,
        page: currPage,
        limit: PAGE_SIZE,
      };
      fetchData(data);
    }
  }, [sort, currPage, inputSearchValue, showAddNewClient == false]);

  const handleClientClick = (clientId) => {
    setLocalStorage(CLIENT_DETAILS_KEY, { id: clientId });
    navigate(pageURLs.viewClientDetailsMob, {
      state: { clientId: clientId },
    });
  };

  const getClientLogo = (firstName, lastName) => {
    let clientName = lastName + " " + firstName;
    if (clientName) {
      clientName = clientName.replace(/-/g, "").match(/\b(\w)/g);
    }
    return clientName;
  };

  //Tutorial documentation section
  //Defining tutorial steps
  const [tutorialSteps, setTutorialSteps] = useState([
    {
      target: ".tutorialRowMob",
      disableBeacon: true,
      placement: "left",
      title: "Quickly view all of your clients information",
      icon: IntroductionTutorialIconStep1,
      content:
        "You can view a summary here or select your client to view their full set of inventory and want listings.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".tutorialRowMob",
      disableBeacon: true,
      placement: "left",
      title: "Figure out which listings / properties are performing",
      icon: IntroductionTutorialIconStep2,
      content:
        "Scroll left and right to view more information. This will help you modify your want-listings and inventory to figure out what strategies are worth replicating.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#clientAddMob",
      disableBeacon: true,
      placement: "right",
      title: "Easily add new clients",
      content: "When your clientele grows, you can add more here.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: false,
      spotlightPadding: false,
    },
  ]);
  //Tutorial tooltip
  const TutorialToolTip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    size,
    isLastStep,
    skipProps,
  }) => (
    <>
      <div {...tooltipProps}>
        <DocumentationTutorialMob
          continuous={continuous}
          index={index}
          step={step}
          backProps={backProps}
          skipProps={skipProps}
          primaryProps={primaryProps}
          isLastStep={isLastStep}
          size={size}
          finalStepBtn={"Got It!"}
        />
      </div>
    </>
  );
  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1); //Save status
      setDisplayTutorial(false);
    }
  };

  const handleSkipAll = () => {
    saveTutorialStatusToLocal(tutorialPage);
  };

  const clientDemoData = [
    {
      FirstName: "John",
      LastName: "Smith",
      Email: "johnsmith@gmail.com",
      Inventory: 2,
      IntroSent: 4,
      WantAds: 3,
    },
  ];

  const TutorialTable = () => {
    return (
      <div className="main-table-wrap">
        <div className="table-responsive client-table">
          <Table>
            <thead>
              <tr className="top-sticky">
                <th className="left-sticky">Name</th>
                <th>Email</th>
                <th>Inventory</th>
                <th>Intros sent</th>
                <th>Want-Listings</th>
                <th>Intros received</th>
              </tr>
            </thead>
            {clientDemoData !== null && clientDemoData?.length > 0 && (
              <tbody>
                {clientDemoData.map((clients, i) => {
                  return (
                    <tr key={i} className={i === 0 ? "tutorialRowMob" : ""}>
                      <td className="left-sticky">
                        <div className="profile-img profile-bg-meroon">
                          {getClientLogo(clients.FirstName, clients.LastName)}
                        </div>
                        <a
                          href={void 0}
                          role="button"
                          className="txt-no-underline client-name-style"
                        >
                          {truncate(
                            clients.FirstName + " " + clients.LastName,
                            15,
                          )}
                        </a>
                      </td>
                      <td>{clients.Email}</td>
                      <td>
                        <span className="table-badge table-badge-blue">
                          {clients.Inventory + " stored"}
                        </span>
                      </td>
                      <td>{clients.IntroSent}</td>
                      <td>
                        <span className="table-badge table-badge-grey">
                          {clients.WantAds + " active"}
                        </span>
                      </td>
                      <td>{clients.IntroReceived}</td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="gitcha-main-wrapper my-client-wrap">
      {displayTutorial ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
        />
      ) : (
        ""
      )}
      <div className="content-wrapper">
        <TopHeader
          variant={"threeComp"}
          outerClassname="bg-white"
          topClassName="w-100 top-three-comp-headerborder"
          bottomClassName="w-100"
        >
          <BackLinkButton
            shouldToggleColor={shouldToggleColor}
            to={pageURLs.proDashboard}
            role="button"
          />
          <div className={shouldToggleColor ? "heading-premium" : "heading"}>
            My Clients
          </div>
          {displayTutorial ? (
            <div className="d-flex">
              <div className="joyride-dummy-div" id="clientAddMob"></div>
              <div className="right-mobbtn-wrap">
                <a className="action-btn" onClick={handleAddNewClientShow}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.5 10C2.5 9.65482 2.77982 9.375 3.125 9.375H16.875C17.2202 9.375 17.5 9.65482 17.5 10C17.5 10.3452 17.2202 10.625 16.875 10.625H3.125C2.77982 10.625 2.5 10.3452 2.5 10Z"
                      fill={
                        shouldToggleColor
                          ? themeColors.neutral000
                          : themeColors.neutral890
                      }
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10 2.5C10.3452 2.5 10.625 2.77982 10.625 3.125V16.875C10.625 17.2202 10.3452 17.5 10 17.5C9.65482 17.5 9.375 17.2202 9.375 16.875V3.125C9.375 2.77982 9.65482 2.5 10 2.5Z"
                      fill={
                        shouldToggleColor
                          ? themeColors.neutral000
                          : themeColors.neutral890
                      }
                    />
                  </svg>
                </a>
              </div>
            </div>
          ) : (
            <div className="right-mobbtn-wrap">
              <a className="action-btn" onClick={handleAddNewClientShow}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.5 10C2.5 9.65482 2.77982 9.375 3.125 9.375H16.875C17.2202 9.375 17.5 9.65482 17.5 10C17.5 10.3452 17.2202 10.625 16.875 10.625H3.125C2.77982 10.625 2.5 10.3452 2.5 10Z"
                    fill={
                      shouldToggleColor
                        ? themeColors.neutral000
                        : themeColors.neutral890
                    }
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 2.5C10.3452 2.5 10.625 2.77982 10.625 3.125V16.875C10.625 17.2202 10.3452 17.5 10 17.5C9.65482 17.5 9.375 17.2202 9.375 16.875V3.125C9.375 2.77982 9.65482 2.5 10 2.5Z"
                    fill={
                      shouldToggleColor
                        ? themeColors.neutral000
                        : themeColors.neutral890
                    }
                  />
                </svg>
              </a>
            </div>
          )}
        </TopHeader>
        <div className="home-layout-content">
          {showNoResult ? (
            <div className="client-list-wrap">
              <div>
                <div className="search-block d-flex align-items-center">
                  <div
                    className={`search_wrap_mobile_top input-group ${
                      show ? "fluid-search" : ""
                    }`}
                  >
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={inputSearchValue}
                        onChange={handleSearchChange}
                        placeholder="Search clients"
                        onFocus={() => setShow(true)}
                      />
                      <Button
                        variant="outline-secondary"
                        className="close-btn"
                        onClick={onClickSearchClose}
                      >
                        <i className="close-icon"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="search-btn"
                      >
                        <SearchIcon handleSearchClick={onClickHandler} />
                      </Button>
                    </InputGroup>
                  </div>
                  <Button
                    variant=""
                    className="sort-btn"
                    onClick={SortClientShow}
                  >
                    Sort
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8 8C8 8.13261 7.94732 8.25979 7.85355 8.35355C7.75979 8.44732 7.63261 8.5 7.5 8.5H3C2.86739 8.5 2.74021 8.44732 2.64645 8.35355C2.55268 8.25979 2.5 8.13261 2.5 8C2.5 7.86739 2.55268 7.74021 2.64645 7.64645C2.74021 7.55268 2.86739 7.5 3 7.5H7.5C7.63261 7.5 7.75979 7.55268 7.85355 7.64645C7.94732 7.74021 8 7.86739 8 8ZM3 4.5H11.5C11.6326 4.5 11.7598 4.44732 11.8536 4.35355C11.9473 4.25979 12 4.13261 12 4C12 3.86739 11.9473 3.74021 11.8536 3.64645C11.7598 3.55268 11.6326 3.5 11.5 3.5H3C2.86739 3.5 2.74021 3.55268 2.64645 3.64645C2.55268 3.74021 2.5 3.86739 2.5 4C2.5 4.13261 2.55268 4.25979 2.64645 4.35355C2.74021 4.44732 2.86739 4.5 3 4.5ZM6.5 11.5H3C2.86739 11.5 2.74021 11.5527 2.64645 11.6464C2.55268 11.7402 2.5 11.8674 2.5 12C2.5 12.1326 2.55268 12.2598 2.64645 12.3536C2.74021 12.4473 2.86739 12.5 3 12.5H6.5C6.63261 12.5 6.75979 12.4473 6.85355 12.3536C6.94732 12.2598 7 12.1326 7 12C7 11.8674 6.94732 11.7402 6.85355 11.6464C6.75979 11.5527 6.63261 11.5 6.5 11.5ZM14.3538 10.1462C14.3073 10.0998 14.2522 10.0629 14.1915 10.0377C14.1308 10.0126 14.0657 9.99961 14 9.99961C13.9343 9.99961 13.8692 10.0126 13.8085 10.0377C13.7478 10.0629 13.6927 10.0998 13.6462 10.1462L12 11.7931V7C12 6.86739 11.9473 6.74021 11.8536 6.64645C11.7598 6.55268 11.6326 6.5 11.5 6.5C11.3674 6.5 11.2402 6.55268 11.1464 6.64645C11.0527 6.74021 11 6.86739 11 7V11.7931L9.35375 10.1462C9.25993 10.0524 9.13268 9.99972 9 9.99972C8.86732 9.99972 8.74007 10.0524 8.64625 10.1462C8.55243 10.2401 8.49972 10.3673 8.49972 10.5C8.49972 10.6327 8.55243 10.7599 8.64625 10.8538L11.1462 13.3538C11.1927 13.4002 11.2478 13.4371 11.3085 13.4623C11.3692 13.4874 11.4343 13.5004 11.5 13.5004C11.5657 13.5004 11.6308 13.4874 11.6915 13.4623C11.7522 13.4371 11.8073 13.4002 11.8538 13.3538L14.3538 10.8538C14.4002 10.8073 14.4371 10.7522 14.4623 10.6915C14.4874 10.6308 14.5004 10.5657 14.5004 10.5C14.5004 10.4343 14.4874 10.3692 14.4623 10.3085C14.4371 10.2478 14.4002 10.1927 14.3538 10.1462Z"
                        fill={themeColors.secondary500}
                      />
                    </svg>
                  </Button>
                  <SortClientoffcanvas
                    show={showSortClient}
                    onHide={SortClientClose}
                    handleSort={handleSort}
                  />
                </div>
                {inputSearchValue.length > 0 && itemList?.length == 0 ? (
                  <div className="no-user">
                    Sorry, we couldn’t find a client under {inputSearchValue}
                  </div>
                ) : (
                  <div className="main-table-wrap">
                    <div className="table-responsive client-table">
                      <Table>
                        <thead>
                          <tr className="top-sticky">
                            <th className="left-sticky">Name</th>
                            <th>Email</th>
                            <th>Inventory</th>
                            <th>Intros sent</th>
                            <th>Want-Listings</th>
                            <th>Intros received</th>
                          </tr>
                        </thead>
                        {itemList !== null && itemList?.length > 0 && (
                          <tbody>
                            {itemList.map((clients, i) => {
                              return (
                                <tr
                                  key={i}
                                  className={i === 0 ? "tutorialRowMob" : ""}
                                >
                                  <td className="left-sticky">
                                    <div className="profile-img profile-bg-meroon">
                                      {getClientLogo(
                                        clients.FirstName,
                                        clients.LastName,
                                      )}
                                    </div>
                                    <a
                                      href={void 0}
                                      role="button"
                                      className="txt-no-underline client-name-style"
                                      onClick={() =>
                                        handleClientClick(
                                          _.get(clients, "ClientID", ""),
                                        )
                                      }
                                    >
                                      {truncate(
                                        clients.FirstName +
                                          " " +
                                          clients.LastName,
                                        15,
                                      )}
                                    </a>
                                  </td>
                                  <td>{clients.Email}</td>
                                  <td>
                                    <span className="table-badge table-badge-blue">
                                      {clients.Inventory + " stored"}
                                    </span>
                                  </td>
                                  <td>{clients.IntroSent}</td>
                                  <td>
                                    <span className="table-badge table-badge-grey">
                                      {clients.WantAds + " active"}
                                    </span>
                                  </td>
                                  <td>{clients.IntroReceived}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        )}
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : displayTutorial ? (
            <TutorialTable />
          ) : (
            <div className="no-data no-data-client-height d-flex align-items-center justify-content-center flex-column">
              <h4>You don’t have any clients</h4>
              <p className="m-0 text-center">
                Use the Add button in the top right corner to add your first
                client!
              </p>
            </div>
          )}
        </div>
      </div>
      <AddNewClient
        show={showAddNewClient}
        onHide={handleAddNewClientClose}
        clientDetails={null}
        isEdit={false}
      />
      <LearnIntroductionModal
        show={showTutorialModal}
        onHide={() => setShowTutorialModal(false)}
        handleShowTutorial={() => setDisplayTutorial(true)}
        isFromDashboard={true}
        handleSkipAll={handleSkipAll}
      />
    </div>
  );
};

export default AgentClientsListing;
